import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { AlertCircle, Edit, KeyRound, Plus, Loader2, X, Search } from 'lucide-react'
import { Input } from '../components/ui/Input'
import { SearchInput } from '../components/ui/SearchInput'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select'
import CountryCodeSelector from '../components/ui/CountryCodeSelector'
import authService from '../services/authService'

// Dummy user data for testing
const dummyUsers = [
  {
    user_id: 1,
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '9876543210',
    phone_country_code: '+91',
    role: 'SUPER_ADMIN'
  },
  {
    user_id: 2,
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    phone: '9876543211',
    phone_country_code: '+91',
    role: 'SUPER_ADMIN_USER'
  },
  {
    user_id: 3,
    name: 'Robert Johnson',
    email: 'robert.johnson@example.com',
    phone: '9876543212',
    phone_country_code: '+91',
    role: 'TENANT_ADMIN'
  },
  {
    user_id: 4,
    name: 'Emily Davis',
    email: 'emily.davis@example.com',
    phone: '9876543213',
    phone_country_code: '+91',
    role: 'HOSPITAL_USER'
  },
  {
    user_id: 5,
    name: 'Michael Brown',
    email: 'michael.brown@example.com',
    phone: '9876543214',
    phone_country_code: '+91',
    role: 'DRIVER'
  },
  {
    user_id: 6,
    name: 'Sarah Wilson',
    email: 'sarah.wilson@example.com',
    phone: '9876543215',
    phone_country_code: '+91',
    role: 'TENANT_USER'
  },
  {
    user_id: 7,
    name: 'David Miller',
    email: 'david.miller@example.com',
    phone: '9876543216',
    phone_country_code: '+91',
    role: 'SUPER_ADMIN_USER'
  },
  {
    user_id: 8,
    name: 'Lisa Anderson',
    email: 'lisa.anderson@example.com',
    phone: '9876543217',
    phone_country_code: '+91',
    role: 'HOSPITAL_USER'
  }
]

const SuperAdminMaster = ({ onSuccessMessage }) => {
  const [users, setUsers] = useState(dummyUsers) // Initialize with dummy data
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    phone: '',
    phone_country_code: '+91',
    password: '',
    role: 'SUPER_ADMIN_USER'
  })
  const [showForm, setShowForm] = useState(false)
  const [userErrors, setUserErrors] = useState({})
  const [searchQuery, setSearchQuery] = useState('')
  const formCardRef = useRef(null)

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase().trim();
    return users.filter((user) => {
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const phone = (user.phone || '').toLowerCase();
      const role = (user.role || '').toLowerCase();
      const id = String(user.user_id || '').toLowerCase();
      return (
        name.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        role.includes(query) ||
        id.includes(query)
      );
    });
  }, [users, searchQuery]);

  // Fetch users on component mount
  useEffect(() => {
    // Uncomment the line below to fetch real data from API
    // fetchUsers()
    
    // For now, using dummy data
    setUsers(dummyUsers)
    
    // Reset form to create mode
    setUserFormData({
      name: '',
      email: '',
      phone: '',
      phone_country_code: '+91',
      password: '',
      role: 'SUPER_ADMIN_USER'
    })
    setUserErrors({})
    setShowForm(false)
  }, [])

  const fetchUsers = async () => {
    setIsLoadingUsers(true)
    try {
      const response = await authService.getAllUsers()
      if (response.success && response.data) {
        setUsers(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      setUsers([])
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const handleCreateUser = () => {
    setEditingUser(null)
    setUserFormData({
      name: '',
      email: '',
      phone: '',
      phone_country_code: '+91',
      password: '',
      role: 'SUPER_ADMIN_USER'
    })
    setUserErrors({})
    setShowForm(true)
    // Scroll to form immediately
    setTimeout(() => {
      if (formCardRef.current) {
        formCardRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        })
      }
    }, 0)
  }

  const handleEditUser = (user) => {
    console.log('handleEditUser called with:', user)
    if (!user) {
      console.error('No user provided to handleEditUser')
      return
    }
    setEditingUser(user)
    setUserFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      phone_country_code: user.phone_country_code || '+91',
      password: '',
      role: user.role || 'SUPER_ADMIN_USER'
    })
    setUserErrors({})
    setShowForm(true)
    // Scroll to form immediately
    setTimeout(() => {
      if (formCardRef.current) {
        formCardRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        })
      }
    }, 0)
  }

  const handleResetPassword = async (userId) => {
    console.log('handleResetPassword called with userId:', userId)
    if (!userId) {
      console.error('No userId provided to handleResetPassword')
      return
    }
    
    if (!window.confirm('Are you sure you want to reset the password for this user? A temporary password will be generated.')) {
      return
    }
    
    try {
      // For dummy data, just show a success message
      if (users.some(u => u.user_id === userId)) {
        const message = `Password reset successful. Temporary password: TempPass123! (Dummy data)`
        if (onSuccessMessage) {
          onSuccessMessage(message)
        }
        console.log('Password reset simulated for dummy user:', userId)
        return
      }
      
      const response = await authService.resetUserPasswordByAdmin(userId)
      if (response.success) {
        const message = `Password reset successful. Temporary password: ${response.data?.temporaryPassword || 'Check email'}`
        if (onSuccessMessage) {
          onSuccessMessage(message)
        }
        fetchUsers() // Refresh users list
      }
    } catch (error) {
      console.error('Error resetting password:', error)
      const errorMessage = `Error: ${error.message || 'Failed to reset password'}`
      if (onSuccessMessage) {
        onSuccessMessage(errorMessage)
      }
    }
  }

  const handleUserFormSubmit = async (e) => {
    e.preventDefault()
    setUserErrors({})

    // Validation
    if (!userFormData.name || !userFormData.email) {
      setUserErrors({ general: 'Name and email are required' })
      return
    }

    if (!editingUser && !userFormData.password) {
      setUserErrors({ general: 'Password is required for new users' })
      return
    }

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: userFormData.name,
          email: userFormData.email,
          phone: userFormData.phone,
          phone_country_code: userFormData.phone_country_code,
          role: userFormData.role
        }
        if (userFormData.password) {
          updateData.password = userFormData.password
        }
        await authService.updateUser(editingUser.user_id, updateData)
        if (onSuccessMessage) {
          onSuccessMessage('User updated successfully!')
        }
      } else {
        // Create new user
        await authService.createUserByAdmin(userFormData)
        if (onSuccessMessage) {
          onSuccessMessage('User created successfully!')
        }
      }
      // Reset form after successful save
      setEditingUser(null)
      setUserFormData({
        name: '',
        email: '',
        phone: '',
        phone_country_code: '+91',
        password: '',
        role: 'SUPER_ADMIN_USER'
      })
      setUserErrors({})
      setShowForm(false)
      fetchUsers() // Refresh users list
    } catch (error) {
      console.error('Error saving user:', error)
      setUserErrors({ general: error.message || 'Failed to save user' })
    }
  }

  // Render users content based on loading state and data
  const renderUsersContent = () => {
    if (isLoadingUsers) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
          <span className="text-gray-600 text-sm sm:text-base">Loading users...</span>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-sm text-gray-600">No users found.</p>
        </div>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <div className="text-center py-10">
          <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900">No matching users</p>
          <p className="text-xs text-gray-500 mt-1">No users match &ldquo;{searchQuery}&rdquo;.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearchQuery('')}
            className="mt-3 text-xs"
          >
            Clear search
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {/* Desktop/Tablet Table View */}
        <div className="hidden md:block overflow-x-auto -mx-2 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Email</th>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700 hidden lg:table-cell">Phone</th>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Role</th>
                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-3 text-sm font-medium text-gray-900">{user.name || 'N/A'}</td>
                    <td className="p-3 text-sm text-gray-600 break-all">{user.email || 'N/A'}</td>
                    <td className="p-3 text-sm text-gray-600 hidden lg:table-cell">{user.phone || 'N/A'}</td>
                    <td className="p-3 text-sm">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.role || 'N/A'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log('Edit button clicked for user:', user)
                            handleEditUser(user)
                          }}
                          className="text-xs flex items-center gap-1.5 px-2 py-1.5"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          <span className="hidden lg:inline">Edit</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log('Reset Password button clicked for user ID:', user.user_id)
                            handleResetPassword(user.user_id)
                          }}
                          className="text-xs flex items-center gap-1.5 px-2 py-1.5"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          <span className="hidden lg:inline">Reset</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {filteredUsers.map((user) => (
            <Card key={user.user_id} className="border border-gray-200">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{user.name || 'N/A'}</h3>
                    <p className="text-xs text-gray-600 mt-0.5 break-all">{user.email || 'N/A'}</p>
                  </div>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                    {user.role || 'N/A'}
                  </span>
                </div>
                {user.phone && (
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">Phone:</span> {user.phone}
                  </div>
                )}
                <div className="flex flex-col gap-2 pt-2 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      console.log('Edit button clicked for user:', user)
                      handleEditUser(user)
                    }}
                    className="w-full text-xs flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit User
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      console.log('Reset Password button clicked for user ID:', user.user_id)
                      handleResetPassword(user.user_id)
                    }}
                    className="w-full text-xs flex items-center justify-center gap-2"
                  >
                    <KeyRound className="w-4 h-4" />
                    Reset Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex justify-center px-2 sm:px-4 md:px-6 lg:px-8">
      <div className="w-full max-w-8xl space-y-3 sm:space-y-4 md:space-y-6">
        {/* User Form Card - Only visible when showForm is true */}
        {showForm && (
          <Card ref={formCardRef}>
            <CardHeader className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="w-full">
                  <CardTitle className="font-serif text-sm sm:text-base md:text-lg">
                    {editingUser ? 'Edit User' : 'Create New User'}
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    {editingUser ? 'Update user information' : 'Add a new user to the system.'}
                  </CardDescription>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  {editingUser && (
                    <Button 
                      variant="outline"
                      className="text-xs sm:text-sm w-full sm:w-auto flex items-center justify-center gap-2"
                      onClick={handleCreateUser}
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Add New User</span>
                      <span className="sm:hidden">New User</span>
                    </Button>
                  )}
                  <Button 
                    variant="ghost"
                    size="sm"
                    className="text-xs sm:text-sm w-full sm:w-auto flex items-center justify-center gap-2"
                    onClick={() => {
                      setShowForm(false)
                      setEditingUser(null)
                      setUserFormData({
                        name: '',
                        email: '',
                        phone: '',
                        phone_country_code: '+91',
                        password: '',
                        role: 'SUPER_ADMIN_USER'
                      })
                      setUserErrors({})
                    }}
                  >
                    <X className="w-4 h-4" />
                    Close
                  </Button>
                </div>
              </div>
            </CardHeader>
          <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
            {userErrors.general && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <p className="text-sm text-red-600">{userErrors.general}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleUserFormSubmit} className="space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <label htmlFor="user-name" className="block text-sm font-semibold text-gray-900">
                  Name *
                </label>
                <Input
                  id="user-name"
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  placeholder="Enter user name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="user-email" className="block text-sm font-semibold text-gray-900">
                  Email *
                </label>
                <Input
                  id="user-email"
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="user-phone" className="block text-sm font-semibold text-gray-900">
                  Phone
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="w-full sm:w-28">
                    <CountryCodeSelector
                      value={userFormData.phone_country_code}
                      onChange={(e) => setUserFormData({ ...userFormData, phone_country_code: e.target.value })}
                      placeholder="+91"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      id="user-phone"
                      type="tel"
                      value={userFormData.phone}
                      onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="user-role" className="block text-sm font-semibold text-gray-900">
                  Role *
                </label>
                <Select value={userFormData.role} onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                    <SelectItem value="SUPER_ADMIN_USER">Super Admin User</SelectItem>
                    <SelectItem value="TENANT_ADMIN">Tenant Admin</SelectItem>
                    <SelectItem value="TENANT_USER">Tenant User</SelectItem>
                    <SelectItem value="HOSPITAL_USER">Hospital User</SelectItem>
                    <SelectItem value="DRIVER">Driver</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="user-password" className="block text-sm font-semibold text-gray-900">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                </label>
                <Input
                  id="user-password"
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  placeholder={editingUser ? "Enter new password (optional)" : "Enter password"}
                  required={!editingUser}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
                {editingUser && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false)
                      setEditingUser(null)
                      setUserFormData({
                        name: '',
                        email: '',
                        phone: '',
                        phone_country_code: '+91',
                        password: '',
                        role: 'SUPER_ADMIN_USER'
                      })
                      setUserErrors({})
                    }}
                    className="w-full sm:flex-1"
                  >
                    Cancel Edit
                  </Button>
                )}
                <Button
                  type="submit"
                  className={editingUser ? "w-full sm:flex-1" : "w-full"}
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        )}

        {/* Users List Card */}
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
              <div>
                <CardTitle className="font-serif text-sm sm:text-base md:text-lg">All Users</CardTitle>
                <CardDescription className="text-xs sm:text-sm">List of all users in the system.</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                <div className="w-full sm:w-72">
                  <SearchInput
                    placeholder="Search by name, email, phone, role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClear={() => setSearchQuery('')}
                    className="h-9 text-xs sm:text-sm"
                  />
                </div>
                <Button 
                  className="text-xs sm:text-sm w-full sm:w-auto flex items-center justify-center gap-2 whitespace-nowrap h-9"
                  onClick={handleCreateUser}
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Create New User</span>
                  <span className="sm:hidden">New User</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
            {renderUsersContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

SuperAdminMaster.propTypes = {
  onSuccessMessage: PropTypes.func
}

export default SuperAdminMaster

